import styled from 'styled-components'
import theme from '../theme'
import { RelSize, Ratio } from '../mixins'
import { darken, lighten } from 'polished'

export const Base = styled.button`
    border: 0;
    border-radius: ${RelSize(10, 14)};
    box-shadow: 0px 4px 4px 0 rgba(0, 0, 0, 0.1);
    color: ${theme.white};
    cursor: pointer;
    display: inline-block;
    font-size: ${RelSize(14, 16)};
    font-weight: 700;
    line-height: ${RelSize(21, 14)};
    margin: 0;
    ${props => !props.small && `
        padding: ${RelSize(15, 14)} ${RelSize(20, 14)};
    `}
    ${props => props.small && `
        padding: ${RelSize(5, 14)} ${RelSize(15, 14)};
    `}
    position: relative;
	text-align: center;
    transition: all ease-in 0.2s;
    ${props => props.full && `
        width: 100%;
    `}
    &:active, &:focus {
        border: 0;
        box-shadow: inset 2px 2px 2px rgba(0, 0, 0, 0.2);
        outline: none;
    }
	&[type="button"] {
		appearance: initial;
	}
	&[disabled] {
		cursor: default;
		opacity: 0.5;
	}
`;

export const BaseAnchor = styled.a`
	align-items: center;
    border: 0;
    border-radius: ${RelSize(10, 14)};
    box-shadow: 0px 4px 4px 0 rgba(0, 0, 0, 0.1);
    color: ${theme.white};
    cursor: pointer;
    display: flex;
    font-size: ${RelSize(14, 16)};
    font-weight: 700;
	justify-content: center;
    line-height: ${RelSize(21, 14)};
    margin: 0;
    ${props => !props.small && `
        padding: ${RelSize(15, 14)} ${RelSize(20, 14)};
    `}
    ${props => props.small && `
        padding: ${RelSize(5, 14)} ${RelSize(15, 14)};
    `}
    position: relative;
    text-align: center;
    transition: all ease-in 0.2s;
    ${props => props.full && `
        width: 100%;
    `}
    &:active, &:focus {
        border: 0;
        box-shadow: inset 2px 2px 2px rgba(0, 0, 0, 0.2);
        outline: none;
    }
	i {
		margin-right: ${RelSize(10, 14)};
	}
	&[type="button"] {
		appearance: initial;
	}
	&[disabled] {
		cursor: default;
		opacity: 0.5;
		pointer-events: none;
	}
`;

export const SubmitButton = styled(Base)`
    background-color: ${theme.primaryGreen};
    color: ${theme.white};
    &[disabled] {
        background-color: ${lighten(0.2, theme.primaryGreen)};
        cursor: default;
    }
	${props => props.loading && `
		padding-right: ${RelSize(40, 14)};
		&::after {
			content: "";
			position: absolute;
			width: 12px;
			height: 12px;
			top: 50%;
			right: ${RelSize(15, 14)};
			margin-top: -6px;
			border: 2px solid transparent;
			border-top-color: #ffffff;
			border-radius: 50%;
			animation: button-loading-spinner 1s ease infinite;
		}
	`}
`;

export const StandardButtonAnchor = styled(BaseAnchor)`
    background-color: ${theme.primaryGreen};
    color: ${theme.white};
    &:hover {
        background-color: ${darken(0.05, theme.primaryGreen)};
        color: ${darken(0.05, theme.white)};
    }
	${props => props.hasTimesheet && `
		background-repeat: no-repeat;
		background-position: right 10px top 16px;
		background-size: 20px;
		padding-right: ${RelSize(40, 14)};
	`}
	${props => props.nowrap && `
		white-space: nowrap;
	`}
	${props => props.timesheetApproved && `
		background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgdmlld0JveD0iMCAwIDE4IDE4Ij4KICA8ZyBpZD0iR3JvdXBfOTUxNiIgZGF0YS1uYW1lPSJHcm91cCA5NTE2IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMjQyLjk1OCAtNjkyOS41MDIpIj4KICAgIDxjaXJjbGUgaWQ9IkVsbGlwc2VfMTk3IiBkYXRhLW5hbWU9IkVsbGlwc2UgMTk3IiBjeD0iOCIgY3k9IjgiIHI9IjgiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDI0My45NTggNjkzMC41MDIpIiBmaWxsPSJub25lIiBzdHJva2U9IiMxZGFkN2UiIHN0cm9rZS13aWR0aD0iMiIvPgogICAgPHBhdGggaWQ9IlBhdGhfMTA2MjgiIGRhdGEtbmFtZT0iUGF0aCAxMDYyOCIgZD0iTTI1Ny45NTgsNjkzOC4wNjZ2My42NzJsMy4yODYsMy42NzIiIHRyYW5zZm9ybT0idHJhbnNsYXRlKC02LjI3NiAtMy4zOTEpIiBmaWxsPSJub25lIiBzdHJva2U9IiMxZGFkN2UiIHN0cm9rZS13aWR0aD0iMiIvPgogIDwvZz4KPC9zdmc+Cg==");
	`}
	${props => props.timesheetRejected && `
		background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgdmlld0JveD0iMCAwIDE4IDE4Ij4KICA8ZyBpZD0iR3JvdXBfOTUxNiIgZGF0YS1uYW1lPSJHcm91cCA5NTE2IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMjQyLjk1OCAtNjkyOS41MDIpIj4KICAgIDxjaXJjbGUgaWQ9IkVsbGlwc2VfMTk3IiBkYXRhLW5hbWU9IkVsbGlwc2UgMTk3IiBjeD0iOCIgY3k9IjgiIHI9IjgiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDI0My45NTggNjkzMC41MDIpIiBmaWxsPSJub25lIiBzdHJva2U9IiNlMjMxNDEiIHN0cm9rZS13aWR0aD0iMiIvPgogICAgPHBhdGggaWQ9IlBhdGhfMTA2MjgiIGRhdGEtbmFtZT0iUGF0aCAxMDYyOCIgZD0iTTI1Ny45NTgsNjkzOC4wNjZ2My42NzJsMy4yODYsMy42NzIiIHRyYW5zZm9ybT0idHJhbnNsYXRlKC02LjI3NiAtMy4zOTEpIiBmaWxsPSJub25lIiBzdHJva2U9IiNlMjMxNDEiIHN0cm9rZS13aWR0aD0iMiIvPgogIDwvZz4KPC9zdmc+Cg==");
	`}
	${props => props.timesheetPending && `
		background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxOCIgaGVpZ2h0PSIxOCIgdmlld0JveD0iMCAwIDE4IDE4Ij4KICA8ZyBpZD0iR3JvdXBfOTUxNiIgZGF0YS1uYW1lPSJHcm91cCA5NTE2IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMjQyLjk1OCAtNjkyOS41MDIpIj4KICAgIDxjaXJjbGUgaWQ9IkVsbGlwc2VfMTk3IiBkYXRhLW5hbWU9IkVsbGlwc2UgMTk3IiBjeD0iOCIgY3k9IjgiIHI9IjgiIHRyYW5zZm9ybT0idHJhbnNsYXRlKDI0My45NTggNjkzMC41MDIpIiBmaWxsPSJub25lIiBzdHJva2U9IiNmNWJjMDAiIHN0cm9rZS13aWR0aD0iMiIvPgogICAgPHBhdGggaWQ9IlBhdGhfMTA2MjgiIGRhdGEtbmFtZT0iUGF0aCAxMDYyOCIgZD0iTTI1Ny45NTgsNjkzOC4wNjZ2My42NzJsMy4yODYsMy42NzIiIHRyYW5zZm9ybT0idHJhbnNsYXRlKC02LjI3NiAtMy4zOTEpIiBmaWxsPSJub25lIiBzdHJva2U9IiNmNWJjMDAiIHN0cm9rZS13aWR0aD0iMiIvPgogIDwvZz4KPC9zdmc+Cg==");
	`}
	${props => props.pendingLeftApprovedRight && `
		background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxOCAxOCI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImxpbmVhci1ncmFkaWVudCIgeDE9IjAuNSIgeDI9IjAuNSIgeTI9IjAuOTczIiBncmFkaWVudFVuaXRzPSJvYmplY3RCb3VuZGluZ0JveCI+CiAgICAgIDxzdG9wIG9mZnNldD0iMCIgc3RvcC1jb2xvcj0iI2Y1YmMwMCIvPgogICAgICA8c3RvcCBvZmZzZXQ9IjAuNDg4IiBzdG9wLWNvbG9yPSIjZjViYzAwIi8+CiAgICAgIDxzdG9wIG9mZnNldD0iMC41MTciIHN0b3AtY29sb3I9IiNjM2I5MWQiLz4KICAgICAgPHN0b3Agb2Zmc2V0PSIxIiBzdG9wLWNvbG9yPSIjMWRhZDdlIi8+CiAgICA8L2xpbmVhckdyYWRpZW50PgogIDwvZGVmcz4KICA8ZyBpZD0iR3JvdXBfOTUxNiIgZGF0YS1uYW1lPSJHcm91cCA5NTE2IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMjQxLjk1OCAtNjkwNC41MDIpIj4KICAgIDxwYXRoIGlkPSJTdWJ0cmFjdGlvbl8yIiBkYXRhLW5hbWU9IlN1YnRyYWN0aW9uIDIiIGQ9Ik0xMTkuOTU4LDUxOTkuNWE5LDksMCwxLDEsOS05QTkuMDEsOS4wMSwwLDAsMSwxMTkuOTU4LDUxOTkuNVptMC0xNmE3LDcsMCwxLDAsNyw3QTcuMDA4LDcuMDA4LDAsMCwwLDExOS45NTgsNTE4My41WiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTQ5MzkuNTQzIDcwMzMuNDYpIHJvdGF0ZSgtOTApIiBmaWxsPSJ1cmwoI2xpbmVhci1ncmFkaWVudCkiLz4KICAgIDxwYXRoIGlkPSJQYXRoXzEwNzE4IiBkYXRhLW5hbWU9IlBhdGggMTA3MTgiIGQ9Ik0yNTcuOTU4LDY5MzguMDY2djMuNjcybDMuMjg2LDMuNjcyIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtNyAtMjguMjM2KSIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjN2M3YzdjIiBzdHJva2Utd2lkdGg9IjIiLz4KICA8L2c+Cjwvc3ZnPgo=");
	`}
	${props => props.approvedLeftPendingRight && `
		background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxOCAxOCI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImxpbmVhci1ncmFkaWVudCIgeDE9IjAuNSIgeDI9IjAuNSIgeTI9IjAuOTczIiBncmFkaWVudFVuaXRzPSJvYmplY3RCb3VuZGluZ0JveCI+CiAgICAgIDxzdG9wIG9mZnNldD0iMCIgc3RvcC1jb2xvcj0iIzFkYWQ3ZSIvPgogICAgICA8c3RvcCBvZmZzZXQ9IjAuNDg4IiBzdG9wLWNvbG9yPSIjMWRhZDdlIi8+CiAgICAgIDxzdG9wIG9mZnNldD0iMC41MTciIHN0b3AtY29sb3I9IiNmNWJjMDAiLz4KICAgICAgPHN0b3Agb2Zmc2V0PSIxIiBzdG9wLWNvbG9yPSIjZjViYzAwIi8+CiAgICA8L2xpbmVhckdyYWRpZW50PgogIDwvZGVmcz4KICA8ZyBpZD0iR3JvdXBfOTUxNiIgZGF0YS1uYW1lPSJHcm91cCA5NTE2IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMjQxLjk1OCAtNjkwNC41MDIpIj4KICAgIDxwYXRoIGlkPSJTdWJ0cmFjdGlvbl8yIiBkYXRhLW5hbWU9IlN1YnRyYWN0aW9uIDIiIGQ9Ik0xMTkuOTU4LDUxOTkuNWE5LDksMCwxLDEsOS05QTkuMDEsOS4wMSwwLDAsMSwxMTkuOTU4LDUxOTkuNVptMC0xNmE3LDcsMCwxLDAsNyw3QTcuMDA4LDcuMDA4LDAsMCwwLDExOS45NTgsNTE4My41WiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTQ5MzkuNTQzIDcwMzMuNDYpIHJvdGF0ZSgtOTApIiBmaWxsPSJ1cmwoI2xpbmVhci1ncmFkaWVudCkiLz4KICAgIDxwYXRoIGlkPSJQYXRoXzEwNzE4IiBkYXRhLW5hbWU9IlBhdGggMTA3MTgiIGQ9Ik0yNTcuOTU4LDY5MzguMDY2djMuNjcybDMuMjg2LDMuNjcyIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtNyAtMjguMjM2KSIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjN2M3YzdjIiBzdHJva2Utd2lkdGg9IjIiLz4KICA8L2c+Cjwvc3ZnPgo=");
	`}
	${props => props.approvedLeftRejectedRight && `
		background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxOCAxOCI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImxpbmVhci1ncmFkaWVudCIgeDE9IjAuNSIgeDI9IjAuNSIgeTI9IjAuOTczIiBncmFkaWVudFVuaXRzPSJvYmplY3RCb3VuZGluZ0JveCI+CiAgICAgIDxzdG9wIG9mZnNldD0iMCIgc3RvcC1jb2xvcj0iIzFkYWQ3ZSIvPgogICAgICA8c3RvcCBvZmZzZXQ9IjAuNDg4IiBzdG9wLWNvbG9yPSIjMWRhZDdlIi8+CiAgICAgIDxzdG9wIG9mZnNldD0iMC41MTciIHN0b3AtY29sb3I9IiNlMjMxNDEiLz4KICAgICAgPHN0b3Agb2Zmc2V0PSIxIiBzdG9wLWNvbG9yPSIjZTIzMTQxIi8+CiAgICA8L2xpbmVhckdyYWRpZW50PgogIDwvZGVmcz4KICA8ZyBpZD0iR3JvdXBfOTUxNiIgZGF0YS1uYW1lPSJHcm91cCA5NTE2IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMjQxLjk1OCAtNjkwNC41MDIpIj4KICAgIDxwYXRoIGlkPSJTdWJ0cmFjdGlvbl8yIiBkYXRhLW5hbWU9IlN1YnRyYWN0aW9uIDIiIGQ9Ik0xMTkuOTU4LDUxOTkuNWE5LDksMCwxLDEsOS05QTkuMDEsOS4wMSwwLDAsMSwxMTkuOTU4LDUxOTkuNVptMC0xNmE3LDcsMCwxLDAsNyw3QTcuMDA4LDcuMDA4LDAsMCwwLDExOS45NTgsNTE4My41WiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTQ5MzkuNTQzIDcwMzMuNDYpIHJvdGF0ZSgtOTApIiBmaWxsPSJ1cmwoI2xpbmVhci1ncmFkaWVudCkiLz4KICAgIDxwYXRoIGlkPSJQYXRoXzEwNzE4IiBkYXRhLW5hbWU9IlBhdGggMTA3MTgiIGQ9Ik0yNTcuOTU4LDY5MzguMDY2djMuNjcybDMuMjg2LDMuNjcyIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtNyAtMjguMjM2KSIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjN2M3YzdjIiBzdHJva2Utd2lkdGg9IjIiLz4KICA8L2c+Cjwvc3ZnPgo=");
	`}
	${props => props.rejectedLeftApprovedRight && `
		background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxOCAxOCI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImxpbmVhci1ncmFkaWVudCIgeDE9IjAuNSIgeDI9IjAuNSIgeTI9IjAuOTczIiBncmFkaWVudFVuaXRzPSJvYmplY3RCb3VuZGluZ0JveCI+CiAgICAgIDxzdG9wIG9mZnNldD0iMCIgc3RvcC1jb2xvcj0iI2UyMzE0MSIvPgogICAgICA8c3RvcCBvZmZzZXQ9IjAuNDg4IiBzdG9wLWNvbG9yPSIjZTIzMTQxIi8+CiAgICAgIDxzdG9wIG9mZnNldD0iMC41MTciIHN0b3AtY29sb3I9IiMxZGFkN2UiLz4KICAgICAgPHN0b3Agb2Zmc2V0PSIxIiBzdG9wLWNvbG9yPSIjMWRhZDdlIi8+CiAgICA8L2xpbmVhckdyYWRpZW50PgogIDwvZGVmcz4KICA8ZyBpZD0iR3JvdXBfOTUxNiIgZGF0YS1uYW1lPSJHcm91cCA5NTE2IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMjQxLjk1OCAtNjkwNC41MDIpIj4KICAgIDxwYXRoIGlkPSJTdWJ0cmFjdGlvbl8yIiBkYXRhLW5hbWU9IlN1YnRyYWN0aW9uIDIiIGQ9Ik0xMTkuOTU4LDUxOTkuNWE5LDksMCwxLDEsOS05QTkuMDEsOS4wMSwwLDAsMSwxMTkuOTU4LDUxOTkuNVptMC0xNmE3LDcsMCwxLDAsNyw3QTcuMDA4LDcuMDA4LDAsMCwwLDExOS45NTgsNTE4My41WiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTQ5MzkuNTQzIDcwMzMuNDYpIHJvdGF0ZSgtOTApIiBmaWxsPSJ1cmwoI2xpbmVhci1ncmFkaWVudCkiLz4KICAgIDxwYXRoIGlkPSJQYXRoXzEwNzE4IiBkYXRhLW5hbWU9IlBhdGggMTA3MTgiIGQ9Ik0yNTcuOTU4LDY5MzguMDY2djMuNjcybDMuMjg2LDMuNjcyIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtNyAtMjguMjM2KSIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjN2M3YzdjIiBzdHJva2Utd2lkdGg9IjIiLz4KICA8L2c+Cjwvc3ZnPgo=");
	`}
	${props => props.pendingLeftRejectedRight && `
		background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxOCAxOCI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImxpbmVhci1ncmFkaWVudCIgeDE9IjAuNSIgeDI9IjAuNSIgeTI9IjAuOTczIiBncmFkaWVudFVuaXRzPSJvYmplY3RCb3VuZGluZ0JveCI+CiAgICAgIDxzdG9wIG9mZnNldD0iMCIgc3RvcC1jb2xvcj0iI2Y1YmMwMCIvPgogICAgICA8c3RvcCBvZmZzZXQ9IjAuNDg4IiBzdG9wLWNvbG9yPSIjZjViYzAwIi8+CiAgICAgIDxzdG9wIG9mZnNldD0iMC41MTciIHN0b3AtY29sb3I9IiNlMjMxNDEiLz4KICAgICAgPHN0b3Agb2Zmc2V0PSIxIiBzdG9wLWNvbG9yPSIjZTIzMTQxIi8+CiAgICA8L2xpbmVhckdyYWRpZW50PgogIDwvZGVmcz4KICA8ZyBpZD0iR3JvdXBfOTUxNiIgZGF0YS1uYW1lPSJHcm91cCA5NTE2IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMjQxLjk1OCAtNjkwNC41MDIpIj4KICAgIDxwYXRoIGlkPSJTdWJ0cmFjdGlvbl8yIiBkYXRhLW5hbWU9IlN1YnRyYWN0aW9uIDIiIGQ9Ik0xMTkuOTU4LDUxOTkuNWE5LDksMCwxLDEsOS05QTkuMDEsOS4wMSwwLDAsMSwxMTkuOTU4LDUxOTkuNVptMC0xNmE3LDcsMCwxLDAsNyw3QTcuMDA4LDcuMDA4LDAsMCwwLDExOS45NTgsNTE4My41WiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTQ5MzkuNTQzIDcwMzMuNDYpIHJvdGF0ZSgtOTApIiBmaWxsPSJ1cmwoI2xpbmVhci1ncmFkaWVudCkiLz4KICAgIDxwYXRoIGlkPSJQYXRoXzEwNzE4IiBkYXRhLW5hbWU9IlBhdGggMTA3MTgiIGQ9Ik0yNTcuOTU4LDY5MzguMDY2djMuNjcybDMuMjg2LDMuNjcyIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtNyAtMjguMjM2KSIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjN2M3YzdjIiBzdHJva2Utd2lkdGg9IjIiLz4KICA8L2c+Cjwvc3ZnPgo=");
	`}
	${props => props.rejectedLeftPendingRight && `
		background-image: url("data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHhtbG5zOnhsaW5rPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5L3hsaW5rIiB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHZpZXdCb3g9IjAgMCAxOCAxOCI+CiAgPGRlZnM+CiAgICA8bGluZWFyR3JhZGllbnQgaWQ9ImxpbmVhci1ncmFkaWVudCIgeDE9IjAuNSIgeDI9IjAuNSIgeTI9IjAuOTczIiBncmFkaWVudFVuaXRzPSJvYmplY3RCb3VuZGluZ0JveCI+CiAgICAgIDxzdG9wIG9mZnNldD0iMCIgc3RvcC1jb2xvcj0iI2UyMzE0MSIvPgogICAgICA8c3RvcCBvZmZzZXQ9IjAuNDg4IiBzdG9wLWNvbG9yPSIjZTIzMTQxIi8+CiAgICAgIDxzdG9wIG9mZnNldD0iMC41MTciIHN0b3AtY29sb3I9IiNmNWJjMDAiLz4KICAgICAgPHN0b3Agb2Zmc2V0PSIxIiBzdG9wLWNvbG9yPSIjZjViYzAwIi8+CiAgICA8L2xpbmVhckdyYWRpZW50PgogIDwvZGVmcz4KICA8ZyBpZD0iR3JvdXBfOTUxNiIgZGF0YS1uYW1lPSJHcm91cCA5NTE2IiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtMjQxLjk1OCAtNjkwNC41MDIpIj4KICAgIDxwYXRoIGlkPSJTdWJ0cmFjdGlvbl8yIiBkYXRhLW5hbWU9IlN1YnRyYWN0aW9uIDIiIGQ9Ik0xMTkuOTU4LDUxOTkuNWE5LDksMCwxLDEsOS05QTkuMDEsOS4wMSwwLDAsMSwxMTkuOTU4LDUxOTkuNVptMC0xNmE3LDcsMCwxLDAsNyw3QTcuMDA4LDcuMDA4LDAsMCwwLDExOS45NTgsNTE4My41WiIgdHJhbnNmb3JtPSJ0cmFuc2xhdGUoLTQ5MzkuNTQzIDcwMzMuNDYpIHJvdGF0ZSgtOTApIiBmaWxsPSJ1cmwoI2xpbmVhci1ncmFkaWVudCkiLz4KICAgIDxwYXRoIGlkPSJQYXRoXzEwNzE4IiBkYXRhLW5hbWU9IlBhdGggMTA3MTgiIGQ9Ik0yNTcuOTU4LDY5MzguMDY2djMuNjcybDMuMjg2LDMuNjcyIiB0cmFuc2Zvcm09InRyYW5zbGF0ZSgtNyAtMjguMjM2KSIgZmlsbD0ibm9uZSIgc3Ryb2tlPSIjN2M3YzdjIiBzdHJva2Utd2lkdGg9IjIiLz4KICA8L2c+Cjwvc3ZnPgo=");
	`}
	${props => props.xeroConnected && `
		background-color: ${theme.red};
		&:hover {
			background-color: ${darken(0.05, theme.red)};
		}
	`}
	${props => props.xeroDisconnected && `
		background-color: ${theme.green};
		&:hover {
			background-color: ${darken(0.05, theme.green)};
		}
	`}
	${({ icon, small }) => icon && `
		background-repeat: no-repeat;
		background-position: left 10px top 50%;
		background-size: 20px;
		padding-left: ${small ? RelSize(35, 14) : RelSize(40, 14)};
		padding-right: ${small ? RelSize(35, 14) : RelSize(40, 14)};
	`}
	${({ icon }) => icon === "email" && `
		background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjEiIHZpZXdCb3g9IjAgMCAyMCAyMSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIuMDAyOTMgNi4zODRMOS45OTk5MyAxMC4zODJMMTcuOTk2OSA2LjM4NEMxNy45NjczIDUuODc0NDQgMTcuNzQ0IDUuMzk1NDkgMTcuMzcyOCA1LjA0NTIzQzE3LjAwMTUgNC42OTQ5NyAxNi41MTAzIDQuNDk5OTEgMTUuOTk5OSA0LjVIMy45OTk5M0MzLjQ4OTUxIDQuNDk5OTEgMi45OTgzOCA0LjY5NDk3IDIuNjI3MTEgNS4wNDUyM0MyLjI1NTg0IDUuMzk1NDkgMi4wMzI1MyA1Ljg3NDQ0IDIuMDAyOTMgNi4zODRaIiBmaWxsPSIjMjk0MzM4Ii8+CjxwYXRoIGQ9Ik0xOCA4LjYxODA0TDEwIDEyLjYxOEwyIDguNjE4MDRWMTQuNUMyIDE1LjAzMDUgMi4yMTA3MSAxNS41MzkyIDIuNTg1NzkgMTUuOTE0M0MyLjk2MDg2IDE2LjI4OTMgMy40Njk1NyAxNi41IDQgMTYuNUgxNkMxNi41MzA0IDE2LjUgMTcuMDM5MSAxNi4yODkzIDE3LjQxNDIgMTUuOTE0M0MxNy43ODkzIDE1LjUzOTIgMTggMTUuMDMwNSAxOCAxNC41VjguNjE4MDRaIiBmaWxsPSIjMjk0MzM4Ii8+Cjwvc3ZnPgo=");
		&:hover {
			background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjEiIHZpZXdCb3g9IjAgMCAyMCAyMSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIuMDAyOTMgNi4zODRMOS45OTk5MyAxMC4zODJMMTcuOTk2OSA2LjM4NEMxNy45NjczIDUuODc0NDQgMTcuNzQ0IDUuMzk1NDkgMTcuMzcyOCA1LjA0NTIzQzE3LjAwMTUgNC42OTQ5NyAxNi41MTAzIDQuNDk5OTEgMTUuOTk5OSA0LjVIMy45OTk5M0MzLjQ4OTUxIDQuNDk5OTEgMi45OTgzOCA0LjY5NDk3IDIuNjI3MTEgNS4wNDUyM0MyLjI1NTg0IDUuMzk1NDkgMi4wMzI1MyA1Ljg3NDQ0IDIuMDAyOTMgNi4zODRaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTggOC42MTgwNEwxMCAxMi42MThMMiA4LjYxODA0VjE0LjVDMiAxNS4wMzA1IDIuMjEwNzEgMTUuNTM5MiAyLjU4NTc5IDE1LjkxNDNDMi45NjA4NiAxNi4yODkzIDMuNDY5NTcgMTYuNSA0IDE2LjVIMTZDMTYuNTMwNCAxNi41IDE3LjAzOTEgMTYuMjg5MyAxNy40MTQyIDE1LjkxNDNDMTcuNzg5MyAxNS41MzkyIDE4IDE1LjAzMDUgMTggMTQuNVY4LjYxODA0WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cg==");
		}
	`}
	${({ icon }) => icon === "phone" && `
		background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjEiIHZpZXdCb3g9IjAgMCAyMCAyMSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIgMy41QzIgMy4yMzQ3OCAyLjEwNTM2IDIuOTgwNDMgMi4yOTI4OSAyLjc5Mjg5QzIuNDgwNDMgMi42MDUzNiAyLjczNDc4IDIuNSAzIDIuNUg1LjE1M0M1LjM4OTcxIDIuNTAwMTEgNS42MTg3IDIuNTg0MTggNS43OTkyNCAyLjczNzI2QzUuOTc5NzkgMi44OTAzNCA2LjEwMDE4IDMuMTAyNSA2LjEzOSAzLjMzNkw2Ljg3OSA3Ljc3MUM2LjkxNDM2IDcuOTgyMjIgNi44ODA5NyA4LjE5OTIxIDYuNzgzNzYgOC4zOTAwM0M2LjY4NjU1IDguNTgwODUgNi41MzA2NSA4LjczNTQzIDYuMzM5IDguODMxTDQuNzkxIDkuNjA0QzUuMzQ2MTEgMTAuOTc5NyA2LjE3MjgzIDEyLjIyOTMgNy4yMjE3OCAxMy4yNzgyQzguMjcwNzIgMTQuMzI3MiA5LjUyMDM1IDE1LjE1MzkgMTAuODk2IDE1LjcwOUwxMS42NyAxNC4xNjFDMTEuNzY1NSAxMy45Njk1IDExLjkxOTkgMTMuODEzOCAxMi4xMTA2IDEzLjcxNjZDMTIuMzAxMiAxMy42MTk0IDEyLjUxNzkgMTMuNTg1OSAxMi43MjkgMTMuNjIxTDE3LjE2NCAxNC4zNjFDMTcuMzk3NSAxNC4zOTk4IDE3LjYwOTcgMTQuNTIwMiAxNy43NjI3IDE0LjcwMDhDMTcuOTE1OCAxNC44ODEzIDE3Ljk5OTkgMTUuMTEwMyAxOCAxNS4zNDdWMTcuNUMxOCAxNy43NjUyIDE3Ljg5NDYgMTguMDE5NiAxNy43MDcxIDE4LjIwNzFDMTcuNTE5NiAxOC4zOTQ2IDE3LjI2NTIgMTguNSAxNyAxOC41SDE1QzcuODIgMTguNSAyIDEyLjY4IDIgNS41VjMuNVoiIGZpbGw9IiMyOTQzMzgiLz4KPC9zdmc+Cg==");
		&:hover {
			background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjEiIHZpZXdCb3g9IjAgMCAyMCAyMSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIgMy41QzIgMy4yMzQ3OCAyLjEwNTM2IDIuOTgwNDMgMi4yOTI4OSAyLjc5Mjg5QzIuNDgwNDMgMi42MDUzNiAyLjczNDc4IDIuNSAzIDIuNUg1LjE1M0M1LjM4OTcxIDIuNTAwMTEgNS42MTg3IDIuNTg0MTggNS43OTkyNCAyLjczNzI2QzUuOTc5NzkgMi44OTAzNCA2LjEwMDE4IDMuMTAyNSA2LjEzOSAzLjMzNkw2Ljg3OSA3Ljc3MUM2LjkxNDM2IDcuOTgyMjIgNi44ODA5NyA4LjE5OTIxIDYuNzgzNzYgOC4zOTAwM0M2LjY4NjU1IDguNTgwODUgNi41MzA2NSA4LjczNTQzIDYuMzM5IDguODMxTDQuNzkxIDkuNjA0QzUuMzQ2MTEgMTAuOTc5NyA2LjE3MjgzIDEyLjIyOTMgNy4yMjE3OCAxMy4yNzgyQzguMjcwNzIgMTQuMzI3MiA5LjUyMDM1IDE1LjE1MzkgMTAuODk2IDE1LjcwOUwxMS42NyAxNC4xNjFDMTEuNzY1NSAxMy45Njk1IDExLjkxOTkgMTMuODEzOCAxMi4xMTA2IDEzLjcxNjZDMTIuMzAxMiAxMy42MTk0IDEyLjUxNzkgMTMuNTg1OSAxMi43MjkgMTMuNjIxTDE3LjE2NCAxNC4zNjFDMTcuMzk3NSAxNC4zOTk4IDE3LjYwOTcgMTQuNTIwMiAxNy43NjI3IDE0LjcwMDhDMTcuOTE1OCAxNC44ODEzIDE3Ljk5OTkgMTUuMTEwMyAxOCAxNS4zNDdWMTcuNUMxOCAxNy43NjUyIDE3Ljg5NDYgMTguMDE5NiAxNy43MDcxIDE4LjIwNzFDMTcuNTE5NiAxOC4zOTQ2IDE3LjI2NTIgMTguNSAxNyAxOC41SDE1QzcuODIgMTguNSAyIDEyLjY4IDIgNS41VjMuNVoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo=");
		}
	`}
	${({ icon }) => icon === "add" && `
		position: relative;
		&::before {
			content: "+";
			font-size: ${RelSize(20, 14)};
			font-weight: bold;
			left: 10px;
			position: absolute;
			top: 50%;
			transform: translateY(-50%);
		}
	`}
	${({ icon }) => icon === "camera" && `
		background-image: url("data:image/svg+xml;base64,PCFET0NUWVBFIHN2ZyBQVUJMSUMgIi0vL1czQy8vRFREIFNWRyAxLjEvL0VOIiAiaHR0cDovL3d3dy53My5vcmcvR3JhcGhpY3MvU1ZHLzEuMS9EVEQvc3ZnMTEuZHRkIj4KPCEtLSBVcGxvYWRlZCB0bzogU1ZHIFJlcG8sIHd3dy5zdmdyZXBvLmNvbSwgVHJhbnNmb3JtZWQgYnk6IFNWRyBSZXBvIE1peGVyIFRvb2xzIC0tPgo8c3ZnIHdpZHRoPSI4MDBweCIgaGVpZ2h0PSI4MDBweCIgdmlld0JveD0iMCAwIDE2IDE2IiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHN0cm9rZT0iI2ZmZmZmZiI+Cg08ZyBpZD0iU1ZHUmVwb19iZ0NhcnJpZXIiIHN0cm9rZS13aWR0aD0iMCIvPgoNPGcgaWQ9IlNWR1JlcG9fdHJhY2VyQ2FycmllciIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+Cg08ZyBpZD0iU1ZHUmVwb19pY29uQ2FycmllciI+IDxwYXRoIGZpbGwtcnVsZT0iZXZlbm9kZCIgY2xpcC1ydWxlPSJldmVub2RkIiBkPSJNMyAzSDBWMTRIMTZWM0gxM0wxMSAxSDVMMyAzWk04IDExQzkuNjU2ODUgMTEgMTEgOS42NTY4NSAxMSA4QzExIDYuMzQzMTUgOS42NTY4NSA1IDggNUM2LjM0MzE1IDUgNSA2LjM0MzE1IDUgOEM1IDkuNjU2ODUgNi4zNDMxNSAxMSA4IDExWiIgZmlsbD0iI2ZmZmZmZiIvPiA8L2c+Cg08L3N2Zz4=");
	`}
`;

export const AltButtonAnchor = styled(BaseAnchor)`
    background-color: transparent;
    border: 2px solid ${theme.primaryGreen};
    color: ${theme.primaryGreen};
	${props => !props.small && `
        padding: ${RelSize(13, 14)} ${RelSize(18, 14)};
    `}
    ${props => props.small && `
        padding: ${RelSize(3, 14)} ${RelSize(13, 14)};
    `}
	${props => props.nowrap && `
        white-space: nowrap;
    `}
    &:hover {
        background-color: ${theme.primaryGreen};
        color: ${theme.white};
    }
	${({ icon, small }) => icon && `
		background-repeat: no-repeat;
		background-position: left 10px top 50%;
		background-size: 20px;
		padding-left: ${small ? RelSize(35, 14) : RelSize(40, 14)};
		padding-right: ${small ? RelSize(35, 14) : RelSize(40, 14)};
	`}
	${({ icon }) => icon === "email" && `
		background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjEiIHZpZXdCb3g9IjAgMCAyMCAyMSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIuMDAyOTMgNi4zODRMOS45OTk5MyAxMC4zODJMMTcuOTk2OSA2LjM4NEMxNy45NjczIDUuODc0NDQgMTcuNzQ0IDUuMzk1NDkgMTcuMzcyOCA1LjA0NTIzQzE3LjAwMTUgNC42OTQ5NyAxNi41MTAzIDQuNDk5OTEgMTUuOTk5OSA0LjVIMy45OTk5M0MzLjQ4OTUxIDQuNDk5OTEgMi45OTgzOCA0LjY5NDk3IDIuNjI3MTEgNS4wNDUyM0MyLjI1NTg0IDUuMzk1NDkgMi4wMzI1MyA1Ljg3NDQ0IDIuMDAyOTMgNi4zODRaIiBmaWxsPSIjMjk0MzM4Ii8+CjxwYXRoIGQ9Ik0xOCA4LjYxODA0TDEwIDEyLjYxOEwyIDguNjE4MDRWMTQuNUMyIDE1LjAzMDUgMi4yMTA3MSAxNS41MzkyIDIuNTg1NzkgMTUuOTE0M0MyLjk2MDg2IDE2LjI4OTMgMy40Njk1NyAxNi41IDQgMTYuNUgxNkMxNi41MzA0IDE2LjUgMTcuMDM5MSAxNi4yODkzIDE3LjQxNDIgMTUuOTE0M0MxNy43ODkzIDE1LjUzOTIgMTggMTUuMDMwNSAxOCAxNC41VjguNjE4MDRaIiBmaWxsPSIjMjk0MzM4Ii8+Cjwvc3ZnPgo=");
		&:hover {
			background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjEiIHZpZXdCb3g9IjAgMCAyMCAyMSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIuMDAyOTMgNi4zODRMOS45OTk5MyAxMC4zODJMMTcuOTk2OSA2LjM4NEMxNy45NjczIDUuODc0NDQgMTcuNzQ0IDUuMzk1NDkgMTcuMzcyOCA1LjA0NTIzQzE3LjAwMTUgNC42OTQ5NyAxNi41MTAzIDQuNDk5OTEgMTUuOTk5OSA0LjVIMy45OTk5M0MzLjQ4OTUxIDQuNDk5OTEgMi45OTgzOCA0LjY5NDk3IDIuNjI3MTEgNS4wNDUyM0MyLjI1NTg0IDUuMzk1NDkgMi4wMzI1MyA1Ljg3NDQ0IDIuMDAyOTMgNi4zODRaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBkPSJNMTggOC42MTgwNEwxMCAxMi42MThMMiA4LjYxODA0VjE0LjVDMiAxNS4wMzA1IDIuMjEwNzEgMTUuNTM5MiAyLjU4NTc5IDE1LjkxNDNDMi45NjA4NiAxNi4yODkzIDMuNDY5NTcgMTYuNSA0IDE2LjVIMTZDMTYuNTMwNCAxNi41IDE3LjAzOTEgMTYuMjg5MyAxNy40MTQyIDE1LjkxNDNDMTcuNzg5MyAxNS41MzkyIDE4IDE1LjAzMDUgMTggMTQuNVY4LjYxODA0WiIgZmlsbD0id2hpdGUiLz4KPC9zdmc+Cg==");
		}
	`}
	${({ icon }) => icon === "phone" && `
		background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjEiIHZpZXdCb3g9IjAgMCAyMCAyMSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIgMy41QzIgMy4yMzQ3OCAyLjEwNTM2IDIuOTgwNDMgMi4yOTI4OSAyLjc5Mjg5QzIuNDgwNDMgMi42MDUzNiAyLjczNDc4IDIuNSAzIDIuNUg1LjE1M0M1LjM4OTcxIDIuNTAwMTEgNS42MTg3IDIuNTg0MTggNS43OTkyNCAyLjczNzI2QzUuOTc5NzkgMi44OTAzNCA2LjEwMDE4IDMuMTAyNSA2LjEzOSAzLjMzNkw2Ljg3OSA3Ljc3MUM2LjkxNDM2IDcuOTgyMjIgNi44ODA5NyA4LjE5OTIxIDYuNzgzNzYgOC4zOTAwM0M2LjY4NjU1IDguNTgwODUgNi41MzA2NSA4LjczNTQzIDYuMzM5IDguODMxTDQuNzkxIDkuNjA0QzUuMzQ2MTEgMTAuOTc5NyA2LjE3MjgzIDEyLjIyOTMgNy4yMjE3OCAxMy4yNzgyQzguMjcwNzIgMTQuMzI3MiA5LjUyMDM1IDE1LjE1MzkgMTAuODk2IDE1LjcwOUwxMS42NyAxNC4xNjFDMTEuNzY1NSAxMy45Njk1IDExLjkxOTkgMTMuODEzOCAxMi4xMTA2IDEzLjcxNjZDMTIuMzAxMiAxMy42MTk0IDEyLjUxNzkgMTMuNTg1OSAxMi43MjkgMTMuNjIxTDE3LjE2NCAxNC4zNjFDMTcuMzk3NSAxNC4zOTk4IDE3LjYwOTcgMTQuNTIwMiAxNy43NjI3IDE0LjcwMDhDMTcuOTE1OCAxNC44ODEzIDE3Ljk5OTkgMTUuMTEwMyAxOCAxNS4zNDdWMTcuNUMxOCAxNy43NjUyIDE3Ljg5NDYgMTguMDE5NiAxNy43MDcxIDE4LjIwNzFDMTcuNTE5NiAxOC4zOTQ2IDE3LjI2NTIgMTguNSAxNyAxOC41SDE1QzcuODIgMTguNSAyIDEyLjY4IDIgNS41VjMuNVoiIGZpbGw9IiMyOTQzMzgiLz4KPC9zdmc+Cg==");
		&:hover {
			background-image: url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjEiIHZpZXdCb3g9IjAgMCAyMCAyMSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTIgMy41QzIgMy4yMzQ3OCAyLjEwNTM2IDIuOTgwNDMgMi4yOTI4OSAyLjc5Mjg5QzIuNDgwNDMgMi42MDUzNiAyLjczNDc4IDIuNSAzIDIuNUg1LjE1M0M1LjM4OTcxIDIuNTAwMTEgNS42MTg3IDIuNTg0MTggNS43OTkyNCAyLjczNzI2QzUuOTc5NzkgMi44OTAzNCA2LjEwMDE4IDMuMTAyNSA2LjEzOSAzLjMzNkw2Ljg3OSA3Ljc3MUM2LjkxNDM2IDcuOTgyMjIgNi44ODA5NyA4LjE5OTIxIDYuNzgzNzYgOC4zOTAwM0M2LjY4NjU1IDguNTgwODUgNi41MzA2NSA4LjczNTQzIDYuMzM5IDguODMxTDQuNzkxIDkuNjA0QzUuMzQ2MTEgMTAuOTc5NyA2LjE3MjgzIDEyLjIyOTMgNy4yMjE3OCAxMy4yNzgyQzguMjcwNzIgMTQuMzI3MiA5LjUyMDM1IDE1LjE1MzkgMTAuODk2IDE1LjcwOUwxMS42NyAxNC4xNjFDMTEuNzY1NSAxMy45Njk1IDExLjkxOTkgMTMuODEzOCAxMi4xMTA2IDEzLjcxNjZDMTIuMzAxMiAxMy42MTk0IDEyLjUxNzkgMTMuNTg1OSAxMi43MjkgMTMuNjIxTDE3LjE2NCAxNC4zNjFDMTcuMzk3NSAxNC4zOTk4IDE3LjYwOTcgMTQuNTIwMiAxNy43NjI3IDE0LjcwMDhDMTcuOTE1OCAxNC44ODEzIDE3Ljk5OTkgMTUuMTEwMyAxOCAxNS4zNDdWMTcuNUMxOCAxNy43NjUyIDE3Ljg5NDYgMTguMDE5NiAxNy43MDcxIDE4LjIwNzFDMTcuNTE5NiAxOC4zOTQ2IDE3LjI2NTIgMTguNSAxNyAxOC41SDE1QzcuODIgMTguNSAyIDEyLjY4IDIgNS41VjMuNVoiIGZpbGw9IndoaXRlIi8+Cjwvc3ZnPgo=");
		}
	`}
`;

export const StandardCancelAnchor = styled(BaseAnchor)`
    background-color: ${theme.red};
    color: ${theme.white};
    &:hover {
        background-color: ${darken(0.05, theme.red)};
        color: ${darken(0.05, theme.white)};
    }
	${({ disabled }) => disabled && `
		background-color: ${lighten(0.2, theme.red)};
		cursor: default;
		pointer-events: none;
	`}
`;

export const ExportButtonAnchor = styled(BaseAnchor)`
    background-color: ${theme.primaryGreen};
    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iaXNvLTg4NTktMSI/Pg0KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDE5LjAuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPg0KPHN2ZyB2ZXJzaW9uPSIxLjEiIGlkPSJDYXBhXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4Ig0KCSB2aWV3Qm94PSIwIDAgNTEyIDUxMiIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgNTEyIDUxMjsiIHhtbDpzcGFjZT0icHJlc2VydmUiPg0KPGc+DQoJPGc+DQoJCTxwYXRoIGQ9Ik00OTIuNzAzLDBIMzUzLjEyNmMtMTAuNjU4LDAtMTkuMjk2LDguNjM4LTE5LjI5NiwxOS4yOTdjMCwxMC42NTgsOC42MzgsMTkuMjk2LDE5LjI5NiwxOS4yOTZoMTIwLjI4MXYxMjAuMjgxDQoJCQljMCwxMC42NTgsOC42MzgsMTkuMjk2LDE5LjI5NiwxOS4yOTZjMTAuNjU4LDAsMTkuMjk3LTguNjM4LDE5LjI5Ny0xOS4yOTZWMTkuMjk3QzUxMiw4LjYzOCw1MDMuMzYyLDAsNDkyLjcwMywweiIgZmlsbD0iI2ZmZiIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KCTxnPg0KCQk8cGF0aCBkPSJNNTA2LjM0Niw1LjY1NGMtNy41MzgtNy41MzktMTkuNzQ3LTcuNTM5LTI3LjI4NSwwTDIwMy43NjQsMjgwLjk1Yy03LjUzOSw3LjUzMi03LjUzOSwxOS43NTMsMCwyNy4yODUNCgkJCWMzLjc2MywzLjc2OSw4LjcwMyw1LjY1NCwxMy42NDMsNS42NTRjNC45MzMsMCw5Ljg3My0xLjg4NSwxMy42NDMtNS42NTRMNTA2LjM0NiwzMi45MzkNCgkJCUM1MTMuODg1LDI1LjQwNyw1MTMuODg1LDEzLjE4Niw1MDYuMzQ2LDUuNjU0eiIgZmlsbD0iI2ZmZiIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KCTxnPg0KCQk8cGF0aCBkPSJNNDI3LjA5NiwyMzkuOTJjLTEwLjY1OCwwLTE5LjI5Nyw4LjYzOC0xOS4yOTcsMTkuMjk2djIxNC4xOTFIMzguNTkzVjEwNC4yMDFoMjE0LjE5MQ0KCQkJYzEwLjY1OCwwLDE5LjI5Ni04LjYzOCwxOS4yOTYtMTkuMjk2cy04LjYzOC0xOS4yOTctMTkuMjk2LTE5LjI5N0gxOS4yOTdDOC42MzgsNjUuNjA4LDAsNzQuMjQ2LDAsODQuOTA1djQwNy43OTkNCgkJCUMwLDUwMy4zNjIsOC42MzgsNTEyLDE5LjI5Nyw1MTJoNDA3Ljc5OWMxMC42NjQsMCwxOS4yOTYtOC42MzgsMTkuMjk2LTE5LjI5N1YyNTkuMjE2DQoJCQlDNDQ2LjM5MiwyNDguNTU4LDQzNy43NTQsMjM5LjkyLDQyNy4wOTYsMjM5LjkyeiIgZmlsbD0iI2ZmZiIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjwvc3ZnPg0K');
	background-size: 15px 15px;
	background-position: 95% 50%;
	background-repeat: no-repeat;
    color: ${theme.white};
    padding-right: ${RelSize(50, 16)};
    &:hover {
        background-color: ${darken(0.05, theme.primaryGreen)};
        color: ${darken(0.05, theme.white)};
    }
`;

export const ExportCtaAnchor = styled(BaseAnchor)`
	${props => props.type === "pdf" ?
		`background-image: url('data:image/svg+xml;base64,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');`
		:
		`background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iaXNvLTg4NTktMSI/Pg0KPHN2ZyB2ZXJzaW9uPSIxLjEiIGlkPSJDYXBhXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4Ig0KCSB2aWV3Qm94PSIwIDAgNTEyIDUxMiIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgNTEyIDUxMjsiIHhtbDpzcGFjZT0icHJlc2VydmUiPg0KPGc+DQoJPGc+DQoJCTxwYXRoIGQ9Ik00OTIuNzAzLDBIMzUzLjEyNmMtMTAuNjU4LDAtMTkuMjk2LDguNjM4LTE5LjI5NiwxOS4yOTdjMCwxMC42NTgsOC42MzgsMTkuMjk2LDE5LjI5NiwxOS4yOTZoMTIwLjI4MXYxMjAuMjgxDQoJCQljMCwxMC42NTgsOC42MzgsMTkuMjk2LDE5LjI5NiwxOS4yOTZjMTAuNjU4LDAsMTkuMjk3LTguNjM4LDE5LjI5Ny0xOS4yOTZWMTkuMjk3QzUxMiw4LjYzOCw1MDMuMzYyLDAsNDkyLjcwMywweiIgZmlsbD0iIzE0MTQxNCIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KCTxnPg0KCQk8cGF0aCBkPSJNNTA2LjM0Niw1LjY1NGMtNy41MzgtNy41MzktMTkuNzQ3LTcuNTM5LTI3LjI4NSwwTDIwMy43NjQsMjgwLjk1Yy03LjUzOSw3LjUzMi03LjUzOSwxOS43NTMsMCwyNy4yODUNCgkJCWMzLjc2MywzLjc2OSw4LjcwMyw1LjY1NCwxMy42NDMsNS42NTRjNC45MzMsMCw5Ljg3My0xLjg4NSwxMy42NDMtNS42NTRMNTA2LjM0NiwzMi45MzkNCgkJCUM1MTMuODg1LDI1LjQwNyw1MTMuODg1LDEzLjE4Niw1MDYuMzQ2LDUuNjU0eiIgZmlsbD0iIzE0MTQxNCIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KCTxnPg0KCQk8cGF0aCBkPSJNNDI3LjA5NiwyMzkuOTJjLTEwLjY1OCwwLTE5LjI5Nyw4LjYzOC0xOS4yOTcsMTkuMjk2djIxNC4xOTFIMzguNTkzVjEwNC4yMDFoMjE0LjE5MQ0KCQkJYzEwLjY1OCwwLDE5LjI5Ni04LjYzOCwxOS4yOTYtMTkuMjk2cy04LjYzOC0xOS4yOTctMTkuMjk2LTE5LjI5N0gxOS4yOTdDOC42MzgsNjUuNjA4LDAsNzQuMjQ2LDAsODQuOTA1djQwNy43OTkNCgkJCUMwLDUwMy4zNjIsOC42MzgsNTEyLDE5LjI5Nyw1MTJoNDA3Ljc5OWMxMC42NjQsMCwxOS4yOTYtOC42MzgsMTkuMjk2LTE5LjI5N1YyNTkuMjE2DQoJCQlDNDQ2LjM5MiwyNDguNTU4LDQzNy43NTQsMjM5LjkyLDQyNy4wOTYsMjM5LjkyeiIgZmlsbD0iIzE0MTQxNCIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjwvc3ZnPg0K');`
	}
	background-size: 15px 15px;
	background-position: 95% 50%;
	background-repeat: no-repeat;
	box-shadow: none;
    color: ${theme.black};
	margin-bottom: ${RelSize(10, 16)};
    padding: 0 ${RelSize(50, 16)} 0 ${props => props.flush ? 0 : RelSize(50, 16)};
	&:active, &:focus {
        box-shadow: none;
    }
`;

export const SendCtaAnchor = styled(BaseAnchor)`
    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz48IS0tIFVwbG9hZGVkIHRvOiBTVkcgUmVwbywgd3d3LnN2Z3JlcG8uY29tLCBHZW5lcmF0b3I6IFNWRyBSZXBvIE1peGVyIFRvb2xzIC0tPgo8c3ZnIHdpZHRoPSI4MDBweCIgaGVpZ2h0PSI4MDBweCIgdmlld0JveD0iMCAwIDI0IDI0IiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxwYXRoIGQ9Ik0yLjk5NiAxMi41bC0xLjE1NyA4LjgyMSAyMC45NS04LjgyMS0yMC45NS04LjgyMXptMTYuMDI4LS41SDMuOTM5bC0uODgyLTYuNzI0ek0zLjkzOSAxM2gxNS4wODVMMy4wNTcgMTkuNzI0eiIvPjxwYXRoIGZpbGw9Im5vbmUiIGQ9Ik0wIDBoMjR2MjRIMHoiLz48L3N2Zz4=');
	background-size: 15px 15px;
	background-position: 95% 50%;
	background-repeat: no-repeat;
	box-shadow: none;
    color: ${theme.black};
	margin-bottom: ${RelSize(10, 16)};
    padding: 0 ${RelSize(50, 16)} 0 ${props => props.flush ? 0 : RelSize(50, 16)};
	&:active, &:focus {
        box-shadow: none;
    }
`;

export const ExpandCtaAnchor = styled(BaseAnchor)`
    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz48IS0tIFVwbG9hZGVkIHRvOiBTVkcgUmVwbywgd3d3LnN2Z3JlcG8uY29tLCBHZW5lcmF0b3I6IFNWRyBSZXBvIE1peGVyIFRvb2xzIC0tPg0KPHN2ZyB3aWR0aD0iODAwcHgiIGhlaWdodD0iODAwcHgiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxwYXRoIGQ9Ik0xNCAxMEwyMSAzTTIxIDNIMTYuNU0yMSAzVjcuNU0xMCAxNEwzIDIxTTMgMjFINy41TTMgMjFMMyAxNi41IiBzdHJva2U9IiMwMDAwMDAiIHN0cm9rZS13aWR0aD0iMiIgc3Ryb2tlLWxpbmVjYXA9InJvdW5kIiBzdHJva2UtbGluZWpvaW49InJvdW5kIi8+DQo8L3N2Zz4=');
	background-size: 15px 15px;
	background-position: 95% 50%;
	background-repeat: no-repeat;
	box-shadow: none;
    color: ${theme.black};
	margin: ${RelSize(40, 16)} 0 ${RelSize(10, 16)};
    padding: 0 ${RelSize(50, 16)} 0 ${props => props.flush ? 0 : RelSize(50, 16)};
	&:active, &:focus {
        box-shadow: none;
    }
`;

export const AuditCtaAnchor = styled(BaseAnchor)`
    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz48IS0tIFVwbG9hZGVkIHRvOiBTVkcgUmVwbywgd3d3LnN2Z3JlcG8uY29tLCBHZW5lcmF0b3I6IFNWRyBSZXBvIE1peGVyIFRvb2xzIC0tPg0KPHN2ZyB3aWR0aD0iODAwcHgiIGhlaWdodD0iODAwcHgiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxyZWN0IHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgZmlsbD0id2hpdGUiLz4NCjxwYXRoIGQ9Ik00IDlWNi40NzIxNEM0IDYuMTYxNjUgNC4wNzIyOSA1Ljg1NTQyIDQuMjExMTUgNS41Nzc3MUw1IDRIMTBMMTEgNkgyMUMyMS41NTIzIDYgMjIgNi40NDc3MiAyMiA3VjlWMThDMjIgMTkuMTA0NiAyMS4xMDQ2IDIwIDIwIDIwSDE4IiBzdHJva2U9IiMwMDAwMDAiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPg0KPHBhdGggZD0iTTE3LjIzNjIgOUgyLjMwOTI1QzEuNjQ5ODggOSAxLjE3MDk5IDkuNjI2OTggMS4zNDQ0OSAxMC4yNjMxTDMuNTk4MDYgMTguNTI2MkMzLjgzNTM3IDE5LjM5NjQgNC42MjU2OSAyMCA1LjUyNzU5IDIwSDE5LjY5MDhDMjAuMzUwMSAyMCAyMC44MjkgMTkuMzczIDIwLjY1NTUgMTguNzM2OUwxOC4yMDEgOS43MzY4OEMxOC4wODIzIDkuMzAxODIgMTcuNjg3MiA5IDE3LjIzNjIgOVoiIHN0cm9rZT0iIzAwMDAwMCIvPg0KPC9zdmc+');
	background-size: 15px 15px;
	background-position: 95% 50%;
	background-repeat: no-repeat;
	box-shadow: none;
    color: ${theme.black};
	margin-bottom: ${RelSize(10, 16)};
    padding: 0 ${RelSize(50, 16)} 0 ${props => props.flush ? 0 : RelSize(50, 16)};
	&:active, &:focus {
        box-shadow: none;
    }
`;

export const DeleteCtaAnchor = styled(BaseAnchor)`
    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz48IS0tIFVwbG9hZGVkIHRvOiBTVkcgUmVwbywgd3d3LnN2Z3JlcG8uY29tLCBHZW5lcmF0b3I6IFNWRyBSZXBvIE1peGVyIFRvb2xzIC0tPg0KPHN2ZyB3aWR0aD0iODAwcHgiIGhlaWdodD0iODAwcHgiIHZpZXdCb3g9IjAgMCAxMDI0IDEwMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZmlsbD0iIzAwMDAwMCIgZD0iTTE2MCAyNTZIOTZhMzIgMzIgMCAwIDEgMC02NGgyNTZWOTUuOTM2YTMyIDMyIDAgMCAxIDMyLTMyaDI1NmEzMiAzMiAwIDAgMSAzMiAzMlYxOTJoMjU2YTMyIDMyIDAgMSAxIDAgNjRoLTY0djY3MmEzMiAzMiAwIDAgMS0zMiAzMkgxOTJhMzIgMzIgMCAwIDEtMzItMzJWMjU2em00NDgtNjR2LTY0SDQxNnY2NGgxOTJ6TTIyNCA4OTZoNTc2VjI1NkgyMjR2NjQwem0xOTItMTI4YTMyIDMyIDAgMCAxLTMyLTMyVjQxNmEzMiAzMiAwIDAgMSA2NCAwdjMyMGEzMiAzMiAwIDAgMS0zMiAzMnptMTkyIDBhMzIgMzIgMCAwIDEtMzItMzJWNDE2YTMyIDMyIDAgMCAxIDY0IDB2MzIwYTMyIDMyIDAgMCAxLTMyIDMyeiIvPjwvc3ZnPg==');
	background-size: 15px 15px;
	background-position: 95% 50%;
	background-repeat: no-repeat;
	box-shadow: none;
    color: ${theme.black};
	margin-bottom: ${RelSize(10, 16)};
    padding: 0 ${RelSize(50, 16)} 0 ${props => props.flush ? 0 : RelSize(50, 16)};
	&:active, &:focus {
        box-shadow: none;
    }
`;

export const CtaAnchor = styled(BaseAnchor)`
	${props => props.type === "delete" ?
		`
			background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz48IS0tIFVwbG9hZGVkIHRvOiBTVkcgUmVwbywgd3d3LnN2Z3JlcG8uY29tLCBHZW5lcmF0b3I6IFNWRyBSZXBvIE1peGVyIFRvb2xzIC0tPg0KPHN2ZyB3aWR0aD0iODAwcHgiIGhlaWdodD0iODAwcHgiIHZpZXdCb3g9IjAgMCAxMDI0IDEwMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZmlsbD0iIzAwMDAwMCIgZD0iTTE2MCAyNTZIOTZhMzIgMzIgMCAwIDEgMC02NGgyNTZWOTUuOTM2YTMyIDMyIDAgMCAxIDMyLTMyaDI1NmEzMiAzMiAwIDAgMSAzMiAzMlYxOTJoMjU2YTMyIDMyIDAgMSAxIDAgNjRoLTY0djY3MmEzMiAzMiAwIDAgMS0zMiAzMkgxOTJhMzIgMzIgMCAwIDEtMzItMzJWMjU2em00NDgtNjR2LTY0SDQxNnY2NGgxOTJ6TTIyNCA4OTZoNTc2VjI1NkgyMjR2NjQwem0xOTItMTI4YTMyIDMyIDAgMCAxLTMyLTMyVjQxNmEzMiAzMiAwIDAgMSA2NCAwdjMyMGEzMiAzMiAwIDAgMS0zMiAzMnptMTkyIDBhMzIgMzIgMCAwIDEtMzItMzJWNDE2YTMyIDMyIDAgMCAxIDY0IDB2MzIwYTMyIDMyIDAgMCAxLTMyIDMyeiIvPjwvc3ZnPg==');
			background-size: 15px 15px;
		`
		: null}
	${props => props.type === "add" ?
		`
			background-image: url('data:image/svg+xml;base64,PHN2ZyBoZWlnaHQ9IjQyNi42NjY2N3B0IiB2aWV3Qm94PSIwIDAgNDI2LjY2NjY3IDQyNi42NjY2NyIgd2lkdGg9IjQyNi42NjY2N3B0IiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxwYXRoIGZpbD0iIzI5NDMzOCIgZD0ibTQwNS4zMzIwMzEgMTkyaC0xNzAuNjY0MDYydi0xNzAuNjY3OTY5YzAtMTEuNzczNDM3LTkuNTU4NTk0LTIxLjMzMjAzMS0yMS4zMzU5MzgtMjEuMzMyMDMxLTExLjc3MzQzNyAwLTIxLjMzMjAzMSA5LjU1ODU5NC0yMS4zMzIwMzEgMjEuMzMyMDMxdjE3MC42Njc5NjloLTE3MC42Njc5NjljLTExLjc3MzQzNyAwLTIxLjMzMjAzMSA5LjU1ODU5NC0yMS4zMzIwMzEgMjEuMzMyMDMxIDAgMTEuNzc3MzQ0IDkuNTU4NTk0IDIxLjMzNTkzOCAyMS4zMzIwMzEgMjEuMzM1OTM4aDE3MC42Njc5Njl2MTcwLjY2NDA2MmMwIDExLjc3NzM0NCA5LjU1ODU5NCAyMS4zMzU5MzggMjEuMzMyMDMxIDIxLjMzNTkzOCAxMS43NzczNDQgMCAyMS4zMzU5MzgtOS41NTg1OTQgMjEuMzM1OTM4LTIxLjMzNTkzOHYtMTcwLjY2NDA2MmgxNzAuNjY0MDYyYzExLjc3NzM0NCAwIDIxLjMzNTkzOC05LjU1ODU5NCAyMS4zMzU5MzgtMjEuMzM1OTM4IDAtMTEuNzczNDM3LTkuNTU4NTk0LTIxLjMzMjAzMS0yMS4zMzU5MzgtMjEuMzMyMDMxem0wIDAiLz48L3N2Zz4=');
			background-size: 12px 12px;
		`
		: null}
	${props => props.type === "export" ?
		`
			background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iaXNvLTg4NTktMSI/Pg0KPHN2ZyB2ZXJzaW9uPSIxLjEiIGlkPSJDYXBhXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4Ig0KCSB2aWV3Qm94PSIwIDAgNTEyIDUxMiIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgNTEyIDUxMjsiIHhtbDpzcGFjZT0icHJlc2VydmUiPg0KPGc+DQoJPGc+DQoJCTxwYXRoIGQ9Ik00OTIuNzAzLDBIMzUzLjEyNmMtMTAuNjU4LDAtMTkuMjk2LDguNjM4LTE5LjI5NiwxOS4yOTdjMCwxMC42NTgsOC42MzgsMTkuMjk2LDE5LjI5NiwxOS4yOTZoMTIwLjI4MXYxMjAuMjgxDQoJCQljMCwxMC42NTgsOC42MzgsMTkuMjk2LDE5LjI5NiwxOS4yOTZjMTAuNjU4LDAsMTkuMjk3LTguNjM4LDE5LjI5Ny0xOS4yOTZWMTkuMjk3QzUxMiw4LjYzOCw1MDMuMzYyLDAsNDkyLjcwMywweiIgZmlsbD0iIzE0MTQxNCIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KCTxnPg0KCQk8cGF0aCBkPSJNNTA2LjM0Niw1LjY1NGMtNy41MzgtNy41MzktMTkuNzQ3LTcuNTM5LTI3LjI4NSwwTDIwMy43NjQsMjgwLjk1Yy03LjUzOSw3LjUzMi03LjUzOSwxOS43NTMsMCwyNy4yODUNCgkJCWMzLjc2MywzLjc2OSw4LjcwMyw1LjY1NCwxMy42NDMsNS42NTRjNC45MzMsMCw5Ljg3My0xLjg4NSwxMy42NDMtNS42NTRMNTA2LjM0NiwzMi45MzkNCgkJCUM1MTMuODg1LDI1LjQwNyw1MTMuODg1LDEzLjE4Niw1MDYuMzQ2LDUuNjU0eiIgZmlsbD0iIzE0MTQxNCIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KCTxnPg0KCQk8cGF0aCBkPSJNNDI3LjA5NiwyMzkuOTJjLTEwLjY1OCwwLTE5LjI5Nyw4LjYzOC0xOS4yOTcsMTkuMjk2djIxNC4xOTFIMzguNTkzVjEwNC4yMDFoMjE0LjE5MQ0KCQkJYzEwLjY1OCwwLDE5LjI5Ni04LjYzOCwxOS4yOTYtMTkuMjk2cy04LjYzOC0xOS4yOTctMTkuMjk2LTE5LjI5N0gxOS4yOTdDOC42MzgsNjUuNjA4LDAsNzQuMjQ2LDAsODQuOTA1djQwNy43OTkNCgkJCUMwLDUwMy4zNjIsOC42MzgsNTEyLDE5LjI5Nyw1MTJoNDA3Ljc5OWMxMC42NjQsMCwxOS4yOTYtOC42MzgsMTkuMjk2LTE5LjI5N1YyNTkuMjE2DQoJCQlDNDQ2LjM5MiwyNDguNTU4LDQzNy43NTQsMjM5LjkyLDQyNy4wOTYsMjM5LjkyeiIgZmlsbD0iIzE0MTQxNCIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjwvc3ZnPg0K');
			background-size: 15px 15px;
		`
		: null}
	background-position: 95% 50%;
	background-repeat: no-repeat;
	box-shadow: none;
    color: ${theme.black};
	margin-bottom: ${RelSize(10, 16)};
    padding: 0 ${RelSize(50, 16)} 0 ${props => props.flush ? 0 : RelSize(50, 16)};
	&:active, &:focus {
        box-shadow: none;
    }
`;

export const DeleteCtaAnchorSimple = styled.a`
    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz48IS0tIFVwbG9hZGVkIHRvOiBTVkcgUmVwbywgd3d3LnN2Z3JlcG8uY29tLCBHZW5lcmF0b3I6IFNWRyBSZXBvIE1peGVyIFRvb2xzIC0tPg0KPHN2ZyB3aWR0aD0iODAwcHgiIGhlaWdodD0iODAwcHgiIHZpZXdCb3g9IjAgMCAxMDI0IDEwMjQiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHBhdGggZmlsbD0iIzAwMDAwMCIgZD0iTTE2MCAyNTZIOTZhMzIgMzIgMCAwIDEgMC02NGgyNTZWOTUuOTM2YTMyIDMyIDAgMCAxIDMyLTMyaDI1NmEzMiAzMiAwIDAgMSAzMiAzMlYxOTJoMjU2YTMyIDMyIDAgMSAxIDAgNjRoLTY0djY3MmEzMiAzMiAwIDAgMS0zMiAzMkgxOTJhMzIgMzIgMCAwIDEtMzItMzJWMjU2em00NDgtNjR2LTY0SDQxNnY2NGgxOTJ6TTIyNCA4OTZoNTc2VjI1NkgyMjR2NjQwem0xOTItMTI4YTMyIDMyIDAgMCAxLTMyLTMyVjQxNmEzMiAzMiAwIDAgMSA2NCAwdjMyMGEzMiAzMiAwIDAgMS0zMiAzMnptMTkyIDBhMzIgMzIgMCAwIDEtMzItMzJWNDE2YTMyIDMyIDAgMCAxIDY0IDB2MzIwYTMyIDMyIDAgMCAxLTMyIDMyeiIvPjwvc3ZnPg==');
	background-size: 15px 15px;
	background-position: 95% 50%;
	background-repeat: no-repeat;
	box-shadow: none;
    color: ${theme.primaryGreen};
	display: inline-block;
	margin: ${RelSize(10, 16)} 0 0 0;
    padding: 0 ${RelSize(50, 16)} 0 0;
`;

export const EditCtaAnchor = styled.a`
    background-image: url('data:image/svg+xml;base64,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');
	background-size: 12px 12px;
	background-position: 0 45%;
	background-repeat: no-repeat;
	box-shadow: none;
    color: ${theme.black};
	display: inline-block;
	font-weight: 700;
	margin-left: ${props => props.flush ? 0 : RelSize(10, 14)};
	${props => props.pushBottom && `
		margin-bottom: ${RelSize(10, 14)};
	`};
    padding: 0 0 0 ${RelSize(15, 14)};
`;

export const AddCtaAnchor = styled.a`
	background-image: url('data:image/svg+xml;base64,PHN2ZyBoZWlnaHQ9IjQyNi42NjY2N3B0IiB2aWV3Qm94PSIwIDAgNDI2LjY2NjY3IDQyNi42NjY2NyIgd2lkdGg9IjQyNi42NjY2N3B0IiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxwYXRoIGZpbD0iIzI5NDMzOCIgZD0ibTQwNS4zMzIwMzEgMTkyaC0xNzAuNjY0MDYydi0xNzAuNjY3OTY5YzAtMTEuNzczNDM3LTkuNTU4NTk0LTIxLjMzMjAzMS0yMS4zMzU5MzgtMjEuMzMyMDMxLTExLjc3MzQzNyAwLTIxLjMzMjAzMSA5LjU1ODU5NC0yMS4zMzIwMzEgMjEuMzMyMDMxdjE3MC42Njc5NjloLTE3MC42Njc5NjljLTExLjc3MzQzNyAwLTIxLjMzMjAzMSA5LjU1ODU5NC0yMS4zMzIwMzEgMjEuMzMyMDMxIDAgMTEuNzc3MzQ0IDkuNTU4NTk0IDIxLjMzNTkzOCAyMS4zMzIwMzEgMjEuMzM1OTM4aDE3MC42Njc5Njl2MTcwLjY2NDA2MmMwIDExLjc3NzM0NCA5LjU1ODU5NCAyMS4zMzU5MzggMjEuMzMyMDMxIDIxLjMzNTkzOCAxMS43NzczNDQgMCAyMS4zMzU5MzgtOS41NTg1OTQgMjEuMzM1OTM4LTIxLjMzNTkzOHYtMTcwLjY2NDA2MmgxNzAuNjY0MDYyYzExLjc3NzM0NCAwIDIxLjMzNTkzOC05LjU1ODU5NCAyMS4zMzU5MzgtMjEuMzM1OTM4IDAtMTEuNzczNDM3LTkuNTU4NTk0LTIxLjMzMjAzMS0yMS4zMzU5MzgtMjEuMzMyMDMxem0wIDAiLz48L3N2Zz4=');
	background-size: 12px 12px;
	background-position: 0 42%;
	background-repeat: no-repeat;
	box-shadow: none;
    color: ${theme.black};
	display: inline-block;
	font-weight: 700;
	margin-left: ${props => props.flush ? 0 : RelSize(10, 14)};
    padding: 0 0 0 ${RelSize(15, 14)};
`;

export const AnonymiseCtaAnchor = styled(BaseAnchor)`
    background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTZweCIgaGVpZ2h0PSIxNnB4IiB2aWV3Qm94PSIwIDAgMTYgMTYiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CiAgPHBhdGggZmlsbC1ydWxlPSJldmVub2RkIiBjbGlwLXJ1bGU9ImV2ZW5vZGQiIGQ9Ik01LjM3MSAxLjA3MmExIDEgMCAwMC0xLjMyLjYxMkwyLjI4IDdIMWExIDEgMCAwMDAgMmgxNGExIDEgMCAxMDAtMmgtMS4yOGwtMS43Ny01LjMxNmExIDEgMCAwMC0xLjMyLS42MTJMOCAyLjEyMyA1LjM3MSAxLjA3MnpNMTEuNjEzIDdsLTEuMjI2LTMuNjc4LTIuMDE2LjgwNmExIDEgMCAwMS0uNzQyIDBsLTIuMDE2LS44MDZMNC4zODcgN2g3LjIyNnoiIGZpbGw9IiMwMDAiLz4KICA8cGF0aCBkPSJNMiAxMWExIDEgMCAxMDAgMmMuNTUyIDAgLjk4LjQ3NSAxLjI0NC45NTlBMiAyIDAgMDA1IDE1aC41NThhMiAyIDAgMDAxLjg5OC0xLjM2N2wuMTA1LS4zMTdhLjQ2My40NjMgMCAwMS44NzggMGwuMTA1LjMxNkEyIDIgMCAwMDEwLjQ0MSAxNUgxMWEyIDIgMCAwMDEuNzU1LTEuMDQxYy4yNjYtLjQ4NC42OTMtLjk1OSAxLjI0NS0uOTU5YTEgMSAwIDEwMC0ySDJ6IiBmaWxsPSIjMDAwIi8+Cjwvc3ZnPgo=');
	background-size: 15px 15px;
	background-position: 95% 50%;
	background-repeat: no-repeat;
	box-shadow: none;
    color: ${theme.black};
	margin-bottom: ${RelSize(10, 16)};
    padding: 0 ${RelSize(50, 16)} 0 ${props => props.flush ? 0 : RelSize(50, 16)};
	&:active, &:focus {
        box-shadow: none;
    }
`;

export const PreviewCtaAnchor = styled.a`
	align-items: center;
	align-self: flex-start;
    background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0idXRmLTgiPz4KDTwhLS0gVXBsb2FkZWQgdG86IFNWRyBSZXBvLCB3d3cuc3ZncmVwby5jb20sIEdlbmVyYXRvcjogU1ZHIFJlcG8gTWl4ZXIgVG9vbHMgLS0+CjxzdmcgZmlsbD0iIzAwMDAwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIiANCgkgd2lkdGg9IjgwMHB4IiBoZWlnaHQ9IjgwMHB4IiB2aWV3Qm94PSIwIDAgNTIgNTIiIGVuYWJsZS1iYWNrZ3JvdW5kPSJuZXcgMCAwIDUyIDUyIiB4bWw6c3BhY2U9InByZXNlcnZlIj4NCjxnPg0KCTxwYXRoIGQ9Ik01MS44LDI1LjFDNDcuMSwxNS42LDM3LjMsOSwyNiw5UzQuOSwxNS42LDAuMiwyNS4xYy0wLjMsMC42LTAuMywxLjMsMCwxLjhDNC45LDM2LjQsMTQuNyw0MywyNiw0Mw0KCQlzMjEuMS02LjYsMjUuOC0xNi4xQzUyLjEsMjYuMyw1Mi4xLDI1LjcsNTEuOCwyNS4xeiBNMjYsMzdjLTYuMSwwLTExLTQuOS0xMS0xMXM0LjktMTEsMTEtMTFzMTEsNC45LDExLDExUzMyLjEsMzcsMjYsMzd6Ii8+DQoJPHBhdGggZD0iTTI2LDE5Yy0zLjksMC03LDMuMS03LDdzMy4xLDcsNyw3czctMy4xLDctN1MyOS45LDE5LDI2LDE5eiIvPg0KPC9nPg0KPC9zdmc+');
	background-size: 15px 15px;
	background-position: 100% 30%;
	background-repeat: no-repeat;
	box-shadow: none;
    color: ${theme.black};
	display: inline-block;
	flex: 0 1 auto;
	font-size: ${RelSize(14, 16)};
	margin-bottom: ${RelSize(10, 14)};
    padding: 0 ${RelSize(30, 14)} 0 0;
	width: auto;
	&:active, &:focus {
        box-shadow: none;
    }
`;

export const NewTabAnchor = styled.a`
	background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iaXNvLTg4NTktMSI/Pg0KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDE5LjAuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPg0KPHN2ZyB2ZXJzaW9uPSIxLjEiIGlkPSJDYXBhXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4Ig0KCSB2aWV3Qm94PSIwIDAgNTEyIDUxMiIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgNTEyIDUxMjsiIHhtbDpzcGFjZT0icHJlc2VydmUiPg0KPGc+DQoJPGc+DQoJCTxwYXRoIGQ9Ik00OTIuNzAzLDBIMzUzLjEyNmMtMTAuNjU4LDAtMTkuMjk2LDguNjM4LTE5LjI5NiwxOS4yOTdjMCwxMC42NTgsOC42MzgsMTkuMjk2LDE5LjI5NiwxOS4yOTZoMTIwLjI4MXYxMjAuMjgxDQoJCQljMCwxMC42NTgsOC42MzgsMTkuMjk2LDE5LjI5NiwxOS4yOTZjMTAuNjU4LDAsMTkuMjk3LTguNjM4LDE5LjI5Ny0xOS4yOTZWMTkuMjk3QzUxMiw4LjYzOCw1MDMuMzYyLDAsNDkyLjcwMywweiIgZmlsbD0iIzI5NDMzOCIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KCTxnPg0KCQk8cGF0aCBkPSJNNTA2LjM0Niw1LjY1NGMtNy41MzgtNy41MzktMTkuNzQ3LTcuNTM5LTI3LjI4NSwwTDIwMy43NjQsMjgwLjk1Yy03LjUzOSw3LjUzMi03LjUzOSwxOS43NTMsMCwyNy4yODUNCgkJCWMzLjc2MywzLjc2OSw4LjcwMyw1LjY1NCwxMy42NDMsNS42NTRjNC45MzMsMCw5Ljg3My0xLjg4NSwxMy42NDMtNS42NTRMNTA2LjM0NiwzMi45MzkNCgkJCUM1MTMuODg1LDI1LjQwNyw1MTMuODg1LDEzLjE4Niw1MDYuMzQ2LDUuNjU0eiIgZmlsbD0iIzI5NDMzOCIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KCTxnPg0KCQk8cGF0aCBkPSJNNDI3LjA5NiwyMzkuOTJjLTEwLjY1OCwwLTE5LjI5Nyw4LjYzOC0xOS4yOTcsMTkuMjk2djIxNC4xOTFIMzguNTkzVjEwNC4yMDFoMjE0LjE5MQ0KCQkJYzEwLjY1OCwwLDE5LjI5Ni04LjYzOCwxOS4yOTYtMTkuMjk2cy04LjYzOC0xOS4yOTctMTkuMjk2LTE5LjI5N0gxOS4yOTdDOC42MzgsNjUuNjA4LDAsNzQuMjQ2LDAsODQuOTA1djQwNy43OTkNCgkJCUMwLDUwMy4zNjIsOC42MzgsNTEyLDE5LjI5Nyw1MTJoNDA3Ljc5OWMxMC42NjQsMCwxOS4yOTYtOC42MzgsMTkuMjk2LTE5LjI5N1YyNTkuMjE2DQoJCQlDNDQ2LjM5MiwyNDguNTU4LDQzNy43NTQsMjM5LjkyLDQyNy4wOTYsMjM5LjkyeiIgZmlsbD0iIzI5NDMzOCIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjwvc3ZnPg0K');
	background-size: 15px 15px;
	background-position: 95% 50%;
	background-repeat: no-repeat;
    color: ${theme.primaryGreen};
    padding-right: ${RelSize(40, 16)};
`;

export const ViewMoreButton = styled.a`
	background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iaXNvLTg4NTktMSI/Pg0KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDE5LjAuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPg0KPHN2ZyB2ZXJzaW9uPSIxLjEiIGlkPSJDYXBhXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4Ig0KCSB2aWV3Qm94PSIwIDAgNTEyIDUxMiIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgNTEyIDUxMjsiIHhtbDpzcGFjZT0icHJlc2VydmUiPg0KPGc+DQoJPGc+DQoJCTxwYXRoIGQ9Ik00OTIuNzAzLDBIMzUzLjEyNmMtMTAuNjU4LDAtMTkuMjk2LDguNjM4LTE5LjI5NiwxOS4yOTdjMCwxMC42NTgsOC42MzgsMTkuMjk2LDE5LjI5NiwxOS4yOTZoMTIwLjI4MXYxMjAuMjgxDQoJCQljMCwxMC42NTgsOC42MzgsMTkuMjk2LDE5LjI5NiwxOS4yOTZjMTAuNjU4LDAsMTkuMjk3LTguNjM4LDE5LjI5Ny0xOS4yOTZWMTkuMjk3QzUxMiw4LjYzOCw1MDMuMzYyLDAsNDkyLjcwMywweiIgZmlsbD0iIzI5NDMzOCIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KCTxnPg0KCQk8cGF0aCBkPSJNNTA2LjM0Niw1LjY1NGMtNy41MzgtNy41MzktMTkuNzQ3LTcuNTM5LTI3LjI4NSwwTDIwMy43NjQsMjgwLjk1Yy03LjUzOSw3LjUzMi03LjUzOSwxOS43NTMsMCwyNy4yODUNCgkJCWMzLjc2MywzLjc2OSw4LjcwMyw1LjY1NCwxMy42NDMsNS42NTRjNC45MzMsMCw5Ljg3My0xLjg4NSwxMy42NDMtNS42NTRMNTA2LjM0NiwzMi45MzkNCgkJCUM1MTMuODg1LDI1LjQwNyw1MTMuODg1LDEzLjE4Niw1MDYuMzQ2LDUuNjU0eiIgZmlsbD0iIzI5NDMzOCIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KCTxnPg0KCQk8cGF0aCBkPSJNNDI3LjA5NiwyMzkuOTJjLTEwLjY1OCwwLTE5LjI5Nyw4LjYzOC0xOS4yOTcsMTkuMjk2djIxNC4xOTFIMzguNTkzVjEwNC4yMDFoMjE0LjE5MQ0KCQkJYzEwLjY1OCwwLDE5LjI5Ni04LjYzOCwxOS4yOTYtMTkuMjk2cy04LjYzOC0xOS4yOTctMTkuMjk2LTE5LjI5N0gxOS4yOTdDOC42MzgsNjUuNjA4LDAsNzQuMjQ2LDAsODQuOTA1djQwNy43OTkNCgkJCUMwLDUwMy4zNjIsOC42MzgsNTEyLDE5LjI5Nyw1MTJoNDA3Ljc5OWMxMC42NjQsMCwxOS4yOTYtOC42MzgsMTkuMjk2LTE5LjI5N1YyNTkuMjE2DQoJCQlDNDQ2LjM5MiwyNDguNTU4LDQzNy43NTQsMjM5LjkyLDQyNy4wOTYsMjM5LjkyeiIgZmlsbD0iIzI5NDMzOCIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjwvc3ZnPg0K');
	background-size: 15px 15px;
	background-position: 95% 50%;
	background-repeat: no-repeat;
    color: ${props => props.external ? theme.primaryGreen : theme.primaryGreen};
	font-size: ${RelSize(14, 16)};
	line-height: ${Ratio(14)};
	font-weight: 700;
    padding-right: ${RelSize(30, 14)};
`;

export const AddButtonCta = styled.a`
	background-image: url('data:image/svg+xml;base64,PHN2ZyBoZWlnaHQ9IjQyNi42NjY2N3B0IiB2aWV3Qm94PSIwIDAgNDI2LjY2NjY3IDQyNi42NjY2NyIgd2lkdGg9IjQyNi42NjY2N3B0IiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxwYXRoIGZpbD0iIzI5NDMzOCIgZD0ibTQwNS4zMzIwMzEgMTkyaC0xNzAuNjY0MDYydi0xNzAuNjY3OTY5YzAtMTEuNzczNDM3LTkuNTU4NTk0LTIxLjMzMjAzMS0yMS4zMzU5MzgtMjEuMzMyMDMxLTExLjc3MzQzNyAwLTIxLjMzMjAzMSA5LjU1ODU5NC0yMS4zMzIwMzEgMjEuMzMyMDMxdjE3MC42Njc5NjloLTE3MC42Njc5NjljLTExLjc3MzQzNyAwLTIxLjMzMjAzMSA5LjU1ODU5NC0yMS4zMzIwMzEgMjEuMzMyMDMxIDAgMTEuNzc3MzQ0IDkuNTU4NTk0IDIxLjMzNTkzOCAyMS4zMzIwMzEgMjEuMzM1OTM4aDE3MC42Njc5Njl2MTcwLjY2NDA2MmMwIDExLjc3NzM0NCA5LjU1ODU5NCAyMS4zMzU5MzggMjEuMzMyMDMxIDIxLjMzNTkzOCAxMS43NzczNDQgMCAyMS4zMzU5MzgtOS41NTg1OTQgMjEuMzM1OTM4LTIxLjMzNTkzOHYtMTcwLjY2NDA2MmgxNzAuNjY0MDYyYzExLjc3NzM0NCAwIDIxLjMzNTkzOC05LjU1ODU5NCAyMS4zMzU5MzgtMjEuMzM1OTM4IDAtMTEuNzczNDM3LTkuNTU4NTk0LTIxLjMzMjAzMS0yMS4zMzU5MzgtMjEuMzMyMDMxem0wIDAiLz48L3N2Zz4=');
	background-size: 10px 10px;
	background-position: 95% 40%;
	background-repeat: no-repeat;
    color: ${theme.primaryGreen};
	display: block;
	font-size: ${RelSize(14, 16)};
	line-height: ${Ratio(14)};
	font-weight: 700;
    padding-right: ${RelSize(30, 14)};
`;


export const ExportButtonSpan = styled.span`
    a {
        background-color: ${theme.primaryGreen};
        background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iaXNvLTg4NTktMSI/Pg0KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDE5LjAuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPg0KPHN2ZyB2ZXJzaW9uPSIxLjEiIGlkPSJDYXBhXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4Ig0KCSB2aWV3Qm94PSIwIDAgNTEyIDUxMiIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgNTEyIDUxMjsiIHhtbDpzcGFjZT0icHJlc2VydmUiPg0KPGc+DQoJPGc+DQoJCTxwYXRoIGQ9Ik00OTIuNzAzLDBIMzUzLjEyNmMtMTAuNjU4LDAtMTkuMjk2LDguNjM4LTE5LjI5NiwxOS4yOTdjMCwxMC42NTgsOC42MzgsMTkuMjk2LDE5LjI5NiwxOS4yOTZoMTIwLjI4MXYxMjAuMjgxDQoJCQljMCwxMC42NTgsOC42MzgsMTkuMjk2LDE5LjI5NiwxOS4yOTZjMTAuNjU4LDAsMTkuMjk3LTguNjM4LDE5LjI5Ny0xOS4yOTZWMTkuMjk3QzUxMiw4LjYzOCw1MDMuMzYyLDAsNDkyLjcwMywweiIgZmlsbD0iI2ZmZiIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KCTxnPg0KCQk8cGF0aCBkPSJNNTA2LjM0Niw1LjY1NGMtNy41MzgtNy41MzktMTkuNzQ3LTcuNTM5LTI3LjI4NSwwTDIwMy43NjQsMjgwLjk1Yy03LjUzOSw3LjUzMi03LjUzOSwxOS43NTMsMCwyNy4yODUNCgkJCWMzLjc2MywzLjc2OSw4LjcwMyw1LjY1NCwxMy42NDMsNS42NTRjNC45MzMsMCw5Ljg3My0xLjg4NSwxMy42NDMtNS42NTRMNTA2LjM0NiwzMi45MzkNCgkJCUM1MTMuODg1LDI1LjQwNyw1MTMuODg1LDEzLjE4Niw1MDYuMzQ2LDUuNjU0eiIgZmlsbD0iI2ZmZiIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KCTxnPg0KCQk8cGF0aCBkPSJNNDI3LjA5NiwyMzkuOTJjLTEwLjY1OCwwLTE5LjI5Nyw4LjYzOC0xOS4yOTcsMTkuMjk2djIxNC4xOTFIMzguNTkzVjEwNC4yMDFoMjE0LjE5MQ0KCQkJYzEwLjY1OCwwLDE5LjI5Ni04LjYzOCwxOS4yOTYtMTkuMjk2cy04LjYzOC0xOS4yOTctMTkuMjk2LTE5LjI5N0gxOS4yOTdDOC42MzgsNjUuNjA4LDAsNzQuMjQ2LDAsODQuOTA1djQwNy43OTkNCgkJCUMwLDUwMy4zNjIsOC42MzgsNTEyLDE5LjI5Nyw1MTJoNDA3Ljc5OWMxMC42NjQsMCwxOS4yOTYtOC42MzgsMTkuMjk2LTE5LjI5N1YyNTkuMjE2DQoJCQlDNDQ2LjM5MiwyNDguNTU4LDQzNy43NTQsMjM5LjkyLDQyNy4wOTYsMjM5LjkyeiIgZmlsbD0iI2ZmZiIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjwvc3ZnPg0K');
        background-size: 15px 15px;
        background-position: 95% 50%;
        background-repeat: no-repeat;
        border-radius: ${RelSize(10, 14)};
        box-shadow: 0px 4px 4px 0 rgba(0, 0, 0, 0.1);
        color: ${theme.white};
        cursor: pointer;
        display: inline-block;
        font-size: ${RelSize(14, 16)};
        font-weight: 700;
        line-height: ${Ratio(14)};
        margin: 0;
        ${props => !props.small && `
            padding: ${RelSize(15, 14)} ${RelSize(20, 14)};
        `}
        padding-right: ${RelSize(50, 14)};
        position: relative;
        text-align: center;
        transition: all ease-in 0.2s;
        ${props => props.full && `
            width: 100%;
        `}
        &:active, &:focus {
            border: 0;
            box-shadow: inset 2px 2px 2px rgba(0, 0, 0, 0.2);
            outline: none;
        }
        &:hover {
            background-color: ${darken(0.05, theme.primaryGreen)};
            color: ${darken(0.05, theme.white)};
        }
    }
`;

export const ExternalExportButtonSpan = styled.span`
    a {
        background-color: ${theme.blue};
        background-image: url('data:image/svg+xml;base64,PD94bWwgdmVyc2lvbj0iMS4wIiBlbmNvZGluZz0iaXNvLTg4NTktMSI/Pg0KPCEtLSBHZW5lcmF0b3I6IEFkb2JlIElsbHVzdHJhdG9yIDE5LjAuMCwgU1ZHIEV4cG9ydCBQbHVnLUluIC4gU1ZHIFZlcnNpb246IDYuMDAgQnVpbGQgMCkgIC0tPg0KPHN2ZyB2ZXJzaW9uPSIxLjEiIGlkPSJDYXBhXzEiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyIgeG1sbnM6eGxpbms9Imh0dHA6Ly93d3cudzMub3JnLzE5OTkveGxpbmsiIHg9IjBweCIgeT0iMHB4Ig0KCSB2aWV3Qm94PSIwIDAgNTEyIDUxMiIgc3R5bGU9ImVuYWJsZS1iYWNrZ3JvdW5kOm5ldyAwIDAgNTEyIDUxMjsiIHhtbDpzcGFjZT0icHJlc2VydmUiPg0KPGc+DQoJPGc+DQoJCTxwYXRoIGQ9Ik00OTIuNzAzLDBIMzUzLjEyNmMtMTAuNjU4LDAtMTkuMjk2LDguNjM4LTE5LjI5NiwxOS4yOTdjMCwxMC42NTgsOC42MzgsMTkuMjk2LDE5LjI5NiwxOS4yOTZoMTIwLjI4MXYxMjAuMjgxDQoJCQljMCwxMC42NTgsOC42MzgsMTkuMjk2LDE5LjI5NiwxOS4yOTZjMTAuNjU4LDAsMTkuMjk3LTguNjM4LDE5LjI5Ny0xOS4yOTZWMTkuMjk3QzUxMiw4LjYzOCw1MDMuMzYyLDAsNDkyLjcwMywweiIgZmlsbD0iI2ZmZiIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KCTxnPg0KCQk8cGF0aCBkPSJNNTA2LjM0Niw1LjY1NGMtNy41MzgtNy41MzktMTkuNzQ3LTcuNTM5LTI3LjI4NSwwTDIwMy43NjQsMjgwLjk1Yy03LjUzOSw3LjUzMi03LjUzOSwxOS43NTMsMCwyNy4yODUNCgkJCWMzLjc2MywzLjc2OSw4LjcwMyw1LjY1NCwxMy42NDMsNS42NTRjNC45MzMsMCw5Ljg3My0xLjg4NSwxMy42NDMtNS42NTRMNTA2LjM0NiwzMi45MzkNCgkJCUM1MTMuODg1LDI1LjQwNyw1MTMuODg1LDEzLjE4Niw1MDYuMzQ2LDUuNjU0eiIgZmlsbD0iI2ZmZiIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KCTxnPg0KCQk8cGF0aCBkPSJNNDI3LjA5NiwyMzkuOTJjLTEwLjY1OCwwLTE5LjI5Nyw4LjYzOC0xOS4yOTcsMTkuMjk2djIxNC4xOTFIMzguNTkzVjEwNC4yMDFoMjE0LjE5MQ0KCQkJYzEwLjY1OCwwLDE5LjI5Ni04LjYzOCwxOS4yOTYtMTkuMjk2cy04LjYzOC0xOS4yOTctMTkuMjk2LTE5LjI5N0gxOS4yOTdDOC42MzgsNjUuNjA4LDAsNzQuMjQ2LDAsODQuOTA1djQwNy43OTkNCgkJCUMwLDUwMy4zNjIsOC42MzgsNTEyLDE5LjI5Nyw1MTJoNDA3Ljc5OWMxMC42NjQsMCwxOS4yOTYtOC42MzgsMTkuMjk2LTE5LjI5N1YyNTkuMjE2DQoJCQlDNDQ2LjM5MiwyNDguNTU4LDQzNy43NTQsMjM5LjkyLDQyNy4wOTYsMjM5LjkyeiIgZmlsbD0iI2ZmZiIvPg0KCTwvZz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjxnPg0KPC9nPg0KPGc+DQo8L2c+DQo8Zz4NCjwvZz4NCjwvc3ZnPg0K');
        background-size: 15px 15px;
        background-position: 95% 50%;
        background-repeat: no-repeat;
        color: ${theme.white};
        cursor: pointer;
        display: inline-block;
        font-size: ${RelSize(14, 16)};
        font-weight: 700;
        line-height: ${Ratio(14)};
        margin: 0;
        ${props => !props.small && `
            padding: ${RelSize(15, 14)} ${RelSize(20, 14)};
        `}
        padding-right: ${RelSize(50, 14)};
        position: relative;
        text-align: center;
        transition: all ease-in 0.2s;
        ${props => props.full && `
            width: 100%;
        `}
        &:active, &:focus {
            border: 0;
            box-shadow: inset 2px 2px 2px rgba(0, 0, 0, 0.2);
            outline: none;
        }
        &:hover {
            background-color: ${darken(0.05, theme.blue)};
            color: ${darken(0.05, theme.white)};
        }
    }
`;

export const ApproveButtonAnchor = styled(BaseAnchor)`
    background-color: ${theme.green};
	border-radius: 0 !important;
    color: ${theme.white};
	text-align: center !important;
    &:hover {
        background-color: ${darken(0.05, theme.green)};
        color: ${darken(0.05, theme.white)};
    }
	${props => props.active && `
		opacity: 0.5;
	`}
`;

export const QueryButtonAnchor = styled(BaseAnchor)`
    background-color: ${theme.amber};
	border-radius: 0 !important;
    color: ${theme.primaryGreen};
	text-align: center !important;
    &:hover {
        background-color: ${darken(0.05, theme.amber)};
        color: ${darken(0.05, theme.primaryGreen)};
    }
	${props => props.active && `
		opacity: 0.5;
	`}
`;

export const RejectButtonAnchor = styled(BaseAnchor)`
    background-color: ${theme.red};
	border-radius: 0 !important;
    color: ${theme.white};
	text-align: center !important;
    &:hover {
        background-color: ${darken(0.05, theme.red)};
        color: ${darken(0.05, theme.white)};
    }
	${props => props.active && `
		opacity: 0.5;
	`}
`;

export const TableButton = styled.a`
	align-items: center;
	background-color: ${props => props.remove ? theme.red : theme.primaryGreen};
    border: 0;
    border-radius: ${RelSize(5, 12)};
    box-shadow: 0px 4px 4px 0 rgba(0, 0, 0, 0.1);
    color: ${theme.white};
    cursor: pointer;
    display: flex;
    font-size: ${RelSize(12, 16)};
    font-weight: 400;
    line-height: ${RelSize(15, 12)};
    margin: 0;
    padding: ${RelSize(5, 12)} ${RelSize(20, 12)};
    position: relative;
    text-align: center !important;
    transition: all ease-in 0.2s;
	white-space: nowrap;
    &:active, &:focus {
        border: 0;
        box-shadow: inset 2px 2px 2px rgba(0, 0, 0, 0.2);
        outline: none;
    }
    &:hover {
        background-color: ${props => props.remove ? darken(0.05, theme.red) : darken(0.05, theme.primaryGreen)};
        color: ${darken(0.05, theme.white)};
    }
`;

export const TableButtonWrapper = styled.div`
	display: flex;
	flex-shrink: 0;
	margin-top: 5px;
	width: 100%;
	> a {
		&:not(:last-child) {
			margin-right: 5px;
		}
	}
	${CtaAnchor} {
		padding-left: 0 !important;
		text-align: left;
	}
`;

export const OrderButtonWrapper = styled.div`
	align-items: center;
	display: flex;
	justify-content: center;
	width: 100%;
`;

export const OrderButton = styled.button`
	align-items: center;
	background-color: ${theme.primaryGreen};
	border: none;
	border-radius: 50%;
	color: ${theme.white};
	cursor: pointer;
	display: flex;
	font-size: 12px;
	line-height: 20px;
	height: 20px !important;
	justify-content: center;
	width: 20px !important;
	&:hover {
		background-color: ${darken(0.05, theme.primaryGreen)};
		color: ${darken(0.05, theme.white)};
	}
	&:not(:last-child) {
		margin-right: 2px;
	}
`;

export const OrderButtonAnchor = styled(BaseAnchor)`
`;

export const SummaryButtonStyled = styled.a`
	align-items: center;
    border: 0;
    cursor: pointer;
    display: flex !important;
	justify-content: center;
    margin: 0;
    padding: ${RelSize(5, 16)} ${RelSize(5, 16)};
    position: relative;
    text-align: center !important;
    transition: all ease-in 0.2s;
	white-space: nowrap;
    i {
		display: block;
		height: 25px;
		width: 25px;
		svg {
			display: block;
			height: 100%;
			width: 100%;
		}
	}
`;

export const IconButton = styled.a`
	align-items: center;
	align-self: flex-start;
	display: flex;
	font-size: ${RelSize(12, 16)};
	height: ${RelSize(50, 12)};
	justify-content: center;
	padding: ${RelSize(5, 12)};
	flex-grow: 0;
	width: ${RelSize(50, 12)};
	i {
		height: ${RelSize(30, 12)};
		width: ${RelSize(30, 12)};
		svg {
			display: block;
			height: 100%;
			width: 100%;
		}
	}
`;

export const ButtonList = styled.div`
    align-items: ${props => props.column ? "flex-end" : "center"};
    display: flex;
    flex-direction: ${props => props.column ? "column" : "row"};
    justify-content: ${props => props.justify ? props.justify : "flex-start"};
    ${props => props.pushBottom && `
        margin-bottom: ${RelSize(20, 16)};
    `}
    ${props => props.pushTop && `
        margin-top: ${RelSize(20, 16)};
    `}
	width: 100%;
    > * {
		${props => props.column ?
		`width: 100%;`
		:
		`width: auto;`
	}
        &:not(:last-child) {
            ${props => props.column ?
		`margin-bottom: ${RelSize(10, 16)};`

		:
		`margin-right: ${RelSize(10, 16)};`
	}
            
        }
    }
    @media all and (max-width: 768px) {
        > * {
            margin: 0 0 ${RelSize(10, 16)};
            width: 100%;
        }
    }
`;

export const ButtonInnerIcon = styled.i`
	height: ${RelSize(15, 16)};
	width: ${RelSize(15, 16)};
	svg {
		display: block;
		height: 100%;
		width: 100%;
	}
`;

export const ActionDropdownContainer = styled.div`
	display: inline-block;
	margin-bottom: ${({ flush }) => flush ? 0 : RelSize(20, 16)};
	position: relative;
	${({ full }) => full && `
		@media all and (min-width: 1024px) {
			width: 100%;
		}
	`};
`;

export const ActionButton = styled.button`
	background-color: ${({ alt }) => alt ? theme.white : theme.primaryGreen};
	border: ${({ alt }) => alt ? `2px solid ${theme.primaryGreen}` : 0};
    border-radius: ${RelSize(10, 14)};
    box-shadow: 0px 4px 4px 0 rgba(0, 0, 0, 0.1);
    color: ${({ alt }) => alt ? theme.black : theme.white};
    cursor: pointer;
    display: block;
    font-size: ${RelSize(14, 16)};
    font-weight: 700;
    line-height: ${RelSize(21, 14)};
    margin: 0 0 0;
    padding: ${RelSize(15, 14)} ${RelSize(50, 14)} ${RelSize(15, 14)} ${RelSize(20, 14)};
    position: relative;
	text-align: center;
    transition: all ease-in 0.2s;
	${({ full }) => full && `
		@media all and (min-width: 1024px) {
			width: 100%;
		}
	`};
    &:active, &:focus {
        border: ${({ alt }) => alt ? `2px solid ${theme.primaryGreen}` : 0};
        box-shadow: inset 2px 2px 2px rgba(0, 0, 0, 0.2);
        outline: none;
    }
	&[type="button"] {
		appearance: initial;
	}
	&[disabled] {
		cursor: default;
		opacity: 0.5;
	}
	&:hover {
        background-color: ${darken(0.05, theme.primaryGreen)};
        color: ${darken(0.05, theme.white)};
    }
	&::after {
		content: '⋮';
		font-size: 1.5rem;
		line-height: 0.5;
		position: absolute;
		right: ${RelSize(10, 14)};
		transition: all ease-in 0.2s;
		top: 50%;
		transform: translateY(-50%);
		${({ active }) => active && `
			transform: translateY(-50%) rotate(90deg);
		`}
	}
`;

export const ActionButtonWrapper = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: flex-end;
`;

export const ActionDropdownMenu = styled.div`
	background-color: ${theme.white};
	border: 2px solid ${theme.primaryGreen};
	border-radius: 0.375rem;
	box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
	position: absolute;
	right: 0;
	top: 100%;
	top: calc(100% + 10px);
	width: ${RelSize(250, 16)};
	z-index: 10;
`;

export const ActionDropdownItem = styled.a`
	background-color: white;
	border: none;
	color: ${props => props.isDelete ? theme.trafficLightRed : theme.primaryGreen};
	cursor: pointer;
	display: block;
	font-size: ${RelSize(14, 16)};
	padding: ${RelSize(10, 14)};
	text-align: left;
	width: 100%;

	&:hover {
		background-color: ${props => props.isDelete ? theme.trafficLightRed : theme.primaryGreen};
		color: ${theme.white};
	}
`;